import React, { useCallback, useEffect, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import LinearProgress from '@material-ui/core/LinearProgress';
import { StatusManager } from 'common/components/StatusManager';
import { ENDPOINTS } from 'features/quick-entry/constants/endpoints';
import { useQuickEntryContext } from 'features/quick-entry/context';
import { useShowAlert } from 'hooks';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import ConditionalActionWrapper from 'UI/components/atoms/ConditionalWrapper';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FileManager from 'UI/components/organisms/FileManager';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';
import { getErrorMessage, VALIDATION_REGEXS } from 'UI/utils';

import { QuickEntryTextField } from '../QuickEntryTextField';

import {
  CANDIDATE_QUICK_ENTRY_STATUSES,
  COPIES,
  DEFAULT_FORM_VALUES,
  DELETE_API_VERSION,
  FORM_FIELDS,
  SUBMIT_MODE_CONFIG,
  SUCCESS_STATUS_CODES,
  UPLOAD_CATEGORIES,
  WARNING_CLOSE_DIALOG
} from './CandidateQuickEntryDrawer.constants';
import { useStyles } from './CandidateQuickEntryDrawer.styled';
import { buildFormParams, mapQuickEntryFiles } from './CandidateQuickEntryDrawer.utils';

export const CandidateQuickEntryDrawer = ({
  onClose = () => {},
  quickEntryId = null,
  notesTemplate = null
}) => {
  const isEditing = !!quickEntryId;
  const classes = useStyles();
  const { showAlert } = useShowAlert();
  const { refetchCandidateQuickEntries } = useQuickEntryContext();
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isContinueToDataSheetDialogOpen, setIsContinueToDataSheetDialogOpen] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const isSubmitting = isSaving || isSavingDraft;

  const { state } = useFetchWithStatusV2({
    apiInstance: SECONDARY_API_CLIENT,
    endpoint: isEditing ? `${ENDPOINTS.quickEntriesById.replace(':id', quickEntryId)}` : null,
    initialStatus: UIStatus.Default
  });
  const quickEntry = state?.results;
  const initialFiles = Array.isArray(quickEntry?.files) ? mapQuickEntryFiles(quickEntry.files) : [];

  const shouldUseNotesTemplate =
    !isEditing && typeof notesTemplate === 'string' && Boolean(notesTemplate.trim());
  const form = useForm({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      ...(shouldUseNotesTemplate ? { [FORM_FIELDS.notes]: notesTemplate } : {})
    },
    mode: 'onSubmit',
    reValidateMode: 'onBlur'
  });
  const { handleSubmit, register, reset, setValue, unregister } = form;

  useEffect(() => {
    if (!isEditing || !quickEntry) return;

    reset({
      ...DEFAULT_FORM_VALUES,
      [FORM_FIELDS.name]: quickEntry.candidateName ?? '',
      [FORM_FIELDS.email]: quickEntry.candidateEmail ?? '',
      [FORM_FIELDS.notes]: quickEntry.notes ?? '',
      [FORM_FIELDS.files]: mapQuickEntryFiles(quickEntry.files)
    });
  }, [isEditing, quickEntry, reset]);

  useEffect(() => {
    register({ name: FORM_FIELDS.files });

    return () => {
      unregister(FORM_FIELDS.files);
    };
  }, [register, unregister]);

  const handleFilesChanged = useCallback(
    data => {
      const attachmentsChanged = flatMap(map(data, 'files'));
      setValue(FORM_FIELDS.files, attachmentsChanged);
    },
    [setValue]
  );

  const submitEntry = useCallback(
    async (formData, { status }) => {
      const mode = isEditing ? 'edit' : 'create';
      const { action, method, getEndpoint } = SUBMIT_MODE_CONFIG[mode];
      const isDraft = status === CANDIDATE_QUICK_ENTRY_STATUSES.draft;
      const setLoading = isDraft ? setIsSavingDraft : setIsSaving;

      try {
        setLoading(true);

        const finalParams = {
          ...buildFormParams(formData),
          ...(status && { status })
        };
        const response = await SECONDARY_API_CLIENT[method](getEndpoint(quickEntryId), finalParams);

        if (!SUCCESS_STATUS_CODES.includes(response.status)) return;

        setLoading(false);
        refetchCandidateQuickEntries();
        if (isDraft) {
          showAlert({
            severity: 'success',
            title: COPIES.title,
            body: strings.formatString(COPIES.alert.success, { action })
          });
          onClose();
          return;
        }

        setIsContinueToDataSheetDialogOpen(true);
      } catch (error) {
        setLoading(false);
        showAlert({
          body: getErrorMessage(error, COPIES.alert.error),
          title: COPIES.title,
          severity: 'error'
        });
      }
    },
    [isEditing, onClose, quickEntryId, refetchCandidateQuickEntries, showAlert]
  );

  const handleSaveEntry = handleSubmit(formData =>
    submitEntry(formData, {
      status: CANDIDATE_QUICK_ENTRY_STATUSES.draft
    })
  );

  const handleContinueToDataSheet = handleSubmit(formData =>
    submitEntry(formData, {
      status: CANDIDATE_QUICK_ENTRY_STATUSES.readyToProcess
    })
  );

  const handleClose = () => {
    if (isSubmitting) return;

    setIsConfirmationDialogOpen(true);
  };

  const buttonDefinitions = [
    {
      props: {
        disabled: isSubmitting,
        text: COPIES.actions.cancel,
        variant: 'text',
        onClick: handleClose
      }
    },
    {
      mode: 'save',
      props: {
        disabled: isSaving || state.status === UIStatus.Loading,
        isSaving: isSavingDraft,
        onClick: handleSaveEntry,
        text: COPIES.actions.saveEntry,
        variant: 'outlined'
      }
    },
    {
      mode: 'save',
      props: {
        disabled: isSavingDraft || state.status === UIStatus.Loading,
        isSaving,
        text: COPIES.actions.continueToDataSheet,
        variant: 'contained',
        onClick: handleContinueToDataSheet
      }
    }
  ];

  const handleCloseDialog = () => {
    setIsContinueToDataSheetDialogOpen(false);
    onClose();
  };

  const CustomDialogActions = ({ ActionsMapper }) => {
    return (
      <ActionsMapper
        buttons={[
          {
            text: COPIES.continueToDataSheetDialog.actionButton,
            onClick: handleCloseDialog
          }
        ]}
      />
    );
  };

  const handleConfirmDrawerClose = () => {
    setIsConfirmationDialogOpen(false);
    onClose();
  };

  const CustomCloseDialogActions = ({ defaultButtonsProps, ActionsMapper }) => {
    const { onClose: closeDialog } = defaultButtonsProps;

    return (
      <ActionsMapper
        buttons={[
          {
            variant: 'outlined',
            text: WARNING_CLOSE_DIALOG.actions.discardAndExit,
            onClick: handleConfirmDrawerClose
          },
          {
            text: WARNING_CLOSE_DIALOG.actions.cancel,
            onClick: closeDialog
          }
        ]}
      />
    );
  };

  return (
    <FPDrawer
      CardHeaderProps={{ title: COPIES.title }}
      FPDrawerProps={{
        open: true,
        onClose: handleClose,
        classes: { paper: classes.drawerPaper }
      }}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
      hasPaddingContent={false}
      onClose={handleClose}
    >
      <ConditionalActionWrapper
        condition={isEditing}
        wrapper={c => (
          <StatusManager
            status={state?.status}
            slots={{
              Empty: <EmptyPlaceholder subtitle={COPIES.emptyState.title} />,
              Error: <EmptyPlaceholder title={state?.error} />,
              Loading: <LinearProgress />
            }}
          >
            {c}
          </StatusManager>
        )}
      >
        <FormContext {...form}>
          <div className={classes.content}>
            <QuickEntryTextField
              label={COPIES.fields.name}
              name={FORM_FIELDS.name}
              required
              rows={1}
            />
            <QuickEntryTextField
              label={COPIES.fields.email}
              name={FORM_FIELDS.email}
              required
              rows={1}
              rules={{
                pattern: {
                  value: VALIDATION_REGEXS.EMAIL,
                  message: strings.inventory.quickEntry.validations.invalidEmail
                }
              }}
            />
            <QuickEntryTextField
              label={COPIES.fields.notes}
              multiline
              name={FORM_FIELDS.notes}
              rows={12}
            />
            <div className={classes.uploads}>
              <FileManager
                apiVersionForDeleting={DELETE_API_VERSION}
                defaultCategories={UPLOAD_CATEGORIES}
                filesEndpoint={Endpoints.Files}
                initialFiles={initialFiles}
                isNewEntity
                layoutInColumns
                module="quickEntryCandidate"
                onAttachmentsChanged={handleFilesChanged}
                showItemDate
                showItemMenu
                viewItemOnClick
              />
            </div>
          </div>
        </FormContext>
      </ConditionalActionWrapper>

      <When condition={isContinueToDataSheetDialogOpen}>
        <DecisionDialog
          CustomActions={CustomDialogActions}
          isOpened={isContinueToDataSheetDialogOpen}
          message={COPIES.continueToDataSheetDialog.message}
          onClose={handleCloseDialog}
          onConfirm={handleCloseDialog}
          showSeverityIcon
          severity="success"
          title={COPIES.continueToDataSheetDialog.title}
        />
      </When>

      <When condition={isConfirmationDialogOpen}>
        <DecisionDialog
          CustomActions={CustomCloseDialogActions}
          isOpened
          message={WARNING_CLOSE_DIALOG.message}
          onClose={() => setIsConfirmationDialogOpen(false)}
          onConfirm={handleConfirmDrawerClose}
          showSeverityIcon
          title={WARNING_CLOSE_DIALOG.title}
        />
      </When>
    </FPDrawer>
  );
};
